import { useEffect, useRef, useState } from "react";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { X, Camera, Loader2, AlertCircle } from "lucide-react";

/* Fetch product info from Open Food Facts (free, public, no API key) */
async function fetchOpenFoodFacts(barcode) {
  const url = `https://world.openfoodfacts.org/api/v2/product/${barcode}.json`;
  const res = await fetch(url);
  if (!res.ok) throw new Error("Lookup failed");
  const data = await res.json();
  if (!data.product || data.status === 0) throw new Error("Product not found");
  const p = data.product;
  const n = p.nutriments || {};
  // Open Food Facts gives values per 100g — we'll match our library convention
  return {
    barcode,
    name: p.product_name || p.generic_name || `Product ${barcode}`,
    brand: p.brands || "",
    serving: p.serving_size || "100g",
    cal: n["energy-kcal_100g"] || n["energy-kcal_serving"] || Math.round((n["energy_100g"] || 0) / 4.184),
    p: n.proteins_100g || 0,
    c: n.carbohydrates_100g || 0,
    f: n.fat_100g || 0,
    fib: n.fiber_100g || 0,
    sug: n.sugars_100g || 0,
    na: (n.sodium_100g || 0) * 1000 || (n.salt_100g || 0) * 400, // salt to mg sodium
    image: p.image_front_small_url || p.image_url || null,
  };
}

export default function BarcodeScanner({ onClose, onProductFound }) {
  const videoRef = useRef(null);
  const codeReaderRef = useRef(null);
  const controlsRef = useRef(null);
  const [status, setStatus] = useState("requesting"); // requesting | scanning | looking-up | found | error
  const [error, setError] = useState("");
  const [manualBarcode, setManualBarcode] = useState("");
  const [foundProduct, setFoundProduct] = useState(null);

  useEffect(() => {
    let active = true;
    const startScanner = async () => {
      try {
        const codeReader = new BrowserMultiFormatReader();
        codeReaderRef.current = codeReader;
        if (!videoRef.current) return;
        setStatus("scanning");
        const controls = await codeReader.decodeFromVideoDevice(
          undefined,
          videoRef.current,
          (result, err) => {
            if (!active) return;
            if (result) {
              const text = result.getText();
              if (text) {
                handleBarcode(text);
              }
            }
            // ignore "not found" errors which fire continuously
          }
        );
        controlsRef.current = controls;
      } catch (e) {
        if (!active) return;
        setStatus("error");
        setError(
          e?.name === "NotAllowedError"
            ? "Camera access denied. Allow camera in your browser, or enter a barcode manually below."
            : "Couldn't start camera. Try entering a barcode manually below."
        );
      }
    };
    startScanner();
    return () => {
      active = false;
      try {
        controlsRef.current?.stop();
      } catch {
        // ignore
      }
    };
  }, []);

  const stopCamera = () => {
    try {
      controlsRef.current?.stop();
    } catch {
      // ignore
    }
  };

  const handleBarcode = async (barcode) => {
    if (status === "looking-up" || status === "found") return;
    setStatus("looking-up");
    stopCamera();
    try {
      const product = await fetchOpenFoodFacts(barcode);
      setFoundProduct(product);
      setStatus("found");
    } catch (e) {
      setStatus("error");
      setError(
        `Barcode ${barcode}: not found in Open Food Facts. You can add it manually as a custom food.`
      );
    }
  };

  const submitManual = (e) => {
    e?.preventDefault();
    const v = manualBarcode.trim();
    if (!v) return;
    handleBarcode(v);
  };

  const useProduct = () => {
    if (foundProduct) onProductFound(foundProduct);
  };

  return (
    <div
      data-testid="barcode-scanner-modal"
      className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-[#0F0F0F] border-2 border-[#D4AF37] max-w-lg w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold">
              Scan Or Enter
            </div>
            <h3 className="font-display text-3xl text-white">Barcode Lookup</h3>
            <p className="text-xs text-neutral-500 mt-1">
              Powered by Open Food Facts · 3M+ products
            </p>
          </div>
          <button
            data-testid="barcode-close"
            onClick={onClose}
            className="text-neutral-500 hover:text-white"
          >
            <X size={22} />
          </button>
        </div>

        {/* Found product preview */}
        {status === "found" && foundProduct && (
          <div data-testid="barcode-result" className="space-y-4">
            <div className="bg-[#0A0A0A] border border-[#D4AF37] p-4 flex gap-4">
              {foundProduct.image && (
                <img
                  src={foundProduct.image}
                  alt={foundProduct.name}
                  className="w-20 h-20 object-contain bg-white"
                />
              )}
              <div className="flex-1 min-w-0">
                <div className="font-display text-xl text-white leading-tight">
                  {foundProduct.name}
                </div>
                {foundProduct.brand && (
                  <div className="text-xs text-[#D4AF37] mt-0.5">
                    {foundProduct.brand}
                  </div>
                )}
                <div className="text-[10px] text-neutral-500 mt-1">
                  Per 100g · {foundProduct.serving}
                </div>
                <div className="text-xs text-neutral-300 mt-2">
                  <span className="font-display text-lg text-[#D4AF37]">
                    {Math.round(foundProduct.cal)}
                  </span>{" "}
                  kcal · P{Math.round(foundProduct.p)} · C
                  {Math.round(foundProduct.c)} · F{Math.round(foundProduct.f)}
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                data-testid="barcode-use"
                onClick={useProduct}
                className="btn-omni btn-red flex-1"
              >
                Add To Log
              </button>
              <button
                data-testid="barcode-rescan"
                onClick={() => {
                  setFoundProduct(null);
                  setStatus("requesting");
                  setManualBarcode("");
                  // Restart scanner
                  setTimeout(() => window.location.reload(), 100);
                }}
                className="btn-omni btn-outline-gold"
              >
                Scan Another
              </button>
            </div>
          </div>
        )}

        {/* Scanner / lookup view */}
        {status !== "found" && (
          <>
            <div className="relative bg-black aspect-video mb-4 overflow-hidden border border-zinc-800">
              <video
                ref={videoRef}
                data-testid="barcode-video"
                className="w-full h-full object-cover"
                playsInline
                muted
              />
              {status === "scanning" && (
                <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                  <div className="w-3/4 h-1/3 border-2 border-[#D4AF37] shadow-[0_0_30px_rgba(212,175,55,0.5)]" />
                </div>
              )}
              {status === "looking-up" && (
                <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
                  <Loader2 size={32} className="animate-spin text-[#D4AF37]" />
                  <span className="ml-3 text-white font-accent uppercase tracking-widest text-sm">
                    Looking up…
                  </span>
                </div>
              )}
              {status === "requesting" && (
                <div className="absolute inset-0 flex items-center justify-center text-white">
                  <Camera size={32} className="text-[#D4AF37] mr-3" />
                  <span className="font-accent uppercase tracking-widest text-sm">
                    Starting camera…
                  </span>
                </div>
              )}
            </div>

            {error && (
              <div
                data-testid="barcode-error"
                className="bg-[#DC2626]/10 border border-[#DC2626] text-[#EF4444] px-4 py-3 mb-4 text-sm flex items-start gap-2"
              >
                <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={submitManual} className="space-y-2">
              <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-bold block">
                Or Enter Barcode Manually
              </label>
              <div className="flex gap-2">
                <input
                  data-testid="barcode-manual-input"
                  className="input-omni flex-1"
                  placeholder="e.g., 0028400064057"
                  value={manualBarcode}
                  onChange={(e) =>
                    setManualBarcode(e.target.value.replace(/\D/g, ""))
                  }
                  inputMode="numeric"
                />
                <button
                  data-testid="barcode-manual-submit"
                  type="submit"
                  disabled={!manualBarcode.trim() || status === "looking-up"}
                  className="btn-omni btn-red disabled:opacity-50"
                >
                  Look Up
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
